import { FC, useState } from 'react';
import classNames from 'classnames';
import {
  CalendarIcon,
  UploadIcon,
  UserIcon,
  XIcon,
} from '@heroicons/react/outline';

import Signature from '../svgs/Signature';

import Spinner from '../svgs/Spinner';
import { SvgIconComponent } from '../types/svgs';
import { ActivityOrResourceOptions, ContentType } from '../lib/followUp';

import ThumbnailLink from '../svgs/ThumbnailLink';
import ThumbnailImage from '../svgs/ThumbnailImage';
import ThumbnailPDF from '../svgs/ThumbnailPDF';
import ThumbnailPdfForm from '../svgs/ThumbnailPdfForm';
import ThumbnailForm from '../svgs/ThumbnailForm';
import ThumbnailAction from '../svgs/ThumbnailAction';
import ThumbnailAudio from '../svgs/ThumbnailAudio';
import ThumbnailAssessment from '../svgs/ThumbnailAssessment';

import IconButton from './IconButton';
import ThumbnailText from '../svgs/ThumbnailText';
import ThumbnailPDFESign from '../svgs/ThumbnailPDFESign';

function formatSignedByNames(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

const ContentPreview: FC<{
  contentType: ContentType;
  activityOrResourceOption?: ActivityOrResourceOptions;
  title: string;
  description?: string;
  label?: string;
  previewThumbnailImageUrl?: string;
  className?: string;
  disableClick?: boolean;
  onClick?: () => void;
  onClickCloseX?: () => void;
  hasLoader?: boolean;
  toggleActivityOrResourceLabel?: boolean;
  /** When contentType is Form: use orange practitioner thumbnail when true */
  formVariant?: 'client' | 'practitioner';
  /** When formVariant is practitioner: show this provider's name (no avatar) */
  completedByProvider?: { name: string | null };
  /** When formVariant is practitioner: formatted date string for completed date row */
  completedDate?: string;
  /** When formVariant is practitioner and no provider: e.g. "Client" for completed by client */
  completedByLabel?: string;
  /** For PDF uploads: "Practitioner" or "Client" to use compact layout with "Uploaded by X" */
  uploadedByLabel?: string;
  /** For PDF uploads: formatted date for uploaded date row (use with uploadedByLabel) */
  uploadedDate?: string;
  /** When true, do not show the content-type thumbnail/icon on the card (e.g. when section title has the icon) */
  hideThumbnail?: boolean;
  /** When form: list of signer names to show "Signed by X, Y, and Z" at the bottom */
  signedByNames?: string[];
}> = ({
  contentType,
  activityOrResourceOption = ActivityOrResourceOptions.Activity,
  title,
  description,
  label,
  className,
  previewThumbnailImageUrl,
  disableClick = false,
  onClick,
  onClickCloseX,
  hasLoader = false,
  toggleActivityOrResourceLabel = false,
  formVariant = 'client',
  completedByProvider,
  completedDate,
  completedByLabel,
  uploadedByLabel,
  uploadedDate,
  hideThumbnail = false,
  signedByNames,
}) => {
  const [showXIcon, setShowXIcon] = useState(false);

  const handleClose = (e) => {
    e.stopPropagation();
    onClickCloseX();
  };

  // Documents & Files uses a compact metadata layout for completed forms.
  // Attachment pickers (Mail, Follow-up, etc.) should use the large thumbnail layout.
  const hasFormDocumentMetadata =
    contentType === ContentType.Form &&
    (Boolean(completedByLabel) ||
      Boolean(completedByProvider?.name) ||
      Boolean(completedDate) ||
      Boolean(signedByNames?.length));

  return (
    <div
      className={classNames(
        'relative w-full rounded-xl shadow-100',
        'focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-50',
        !disableClick && 'cursor-pointer hover:bg-neutral-100/10',
        !activityOrResourceOption && 'p-4',
        activityOrResourceOption && 'p-0',
        className,
      )}
      // Role, tab index, and onKeyDown are necessary to recreate button accessibility in div
      {...(onClick &&
        !disableClick && {
          onClick,
          role: 'button',
          tabIndex: 0,
          onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onClick();
            }
          },
        })}
    >
      {onClickCloseX && (
        <div
          onMouseOver={() => {
            hasLoader && setShowXIcon(true);
          }}
          onMouseLeave={() => {
            hasLoader && setShowXIcon(false);
          }}
          className="absolute right-0 top-0 z-10 p-2"
        >
          <IconButton
            aria-label="Remove"
            IconComponent={hasLoader && !showXIcon ? Spinner : XIcon}
            className="close-button hover:bg-neutral-50"
            iconClassName="h-3.5 w-3.5 min-w-[14px] min-h-[14px] self-start"
            onClick={handleClose}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleClose(e);
              }
            }}
          />
        </div>
      )}
      {hasFormDocumentMetadata && formVariant === 'client' ? (
        <CompactCardLayout
          icon={
            hideThumbnail ? undefined : (
              <ThumbnailForm className="h-6 max-h-[24px] min-h-[24px] w-6 min-w-[24px] max-w-[24px] shrink-0 rounded-md" />
            )
          }
          title={title}
          rows={
            <>
              {(completedByLabel || completedByProvider?.name) && (
                <div className="flex min-w-0 items-center gap-2">
                  <UserIcon className="h-4 w-4 shrink-0 text-neutral-150" />
                  <span className="shrink-0 text-small-caption text-neutral-110">
                    Completed by {completedByLabel ?? completedByProvider?.name}
                  </span>
                </div>
              )}
              {completedDate && (
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4 shrink-0 text-neutral-150" />
                  <span className="text-small-caption text-neutral-110">
                    {completedDate}
                  </span>
                </div>
              )}
              {signedByNames != null && signedByNames.length > 0 && (
                <div className="flex min-w-0 items-start gap-2">
                  <Signature className="mt-0.5 h-4 w-4 shrink-0 text-neutral-150" />
                  <span className="text-small-caption text-neutral-110 line-clamp-2">
                    Signed by {formatSignedByNames(signedByNames)}
                  </span>
                </div>
              )}
            </>
          }
        />
      ) : hasFormDocumentMetadata && formVariant === 'practitioner' ? (
        <CompactCardLayout
          icon={
            hideThumbnail ? undefined : (
              <ThumbnailForm
                className="h-6 max-h-[24px] min-h-[24px] w-6 min-w-[24px] max-w-[24px] shrink-0 rounded-md"
                isProviderOnly
              />
            )
          }
          title={title}
          rows={
            <>
              {(completedByProvider?.name || completedByLabel) && (
                <div className="flex min-w-0 items-center gap-2">
                  <UserIcon className="h-4 w-4 shrink-0 text-neutral-150" />
                  <span className="shrink-0 text-small-caption text-neutral-110">
                    Completed by {completedByProvider?.name ?? completedByLabel}
                  </span>
                </div>
              )}
              {completedDate && (
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4 shrink-0 text-neutral-150" />
                  <span className="text-small-caption text-neutral-110">
                    {completedDate}
                  </span>
                </div>
              )}
              {signedByNames != null && signedByNames.length > 0 && (
                <div className="flex min-w-0 items-start gap-2">
                  <Signature className="mt-0.5 h-4 w-4 shrink-0 text-neutral-150" />
                  <span className="text-small-caption text-neutral-110 line-clamp-2">
                    Signed by {formatSignedByNames(signedByNames)}
                  </span>
                </div>
              )}
            </>
          }
        />
      ) : (uploadedByLabel != null || uploadedDate != null) &&
        contentType === ContentType.PDF ? (
        <CompactCardLayout
          icon={
            hideThumbnail ? undefined : (
              <ThumbnailPDF className="h-6 max-h-[24px] min-h-[24px] w-6 min-w-[24px] max-w-[24px] shrink-0 rounded-md" />
            )
          }
          title={title}
          label={label}
          rows={
            <>
              {uploadedByLabel && (
                <div className="flex min-w-0 items-center gap-2">
                  <UploadIcon className="h-4 w-4 shrink-0 text-neutral-150" />
                  <span className="shrink-0 text-small-caption text-neutral-110">
                    Uploaded by {uploadedByLabel}
                  </span>
                </div>
              )}
              {uploadedDate && (
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4 shrink-0 text-neutral-150" />
                  <span className="text-small-caption text-neutral-110">
                    {uploadedDate}
                  </span>
                </div>
              )}
            </>
          }
        />
      ) : (
        <div className="flex flex-row items-center justify-start">
          {!hideThumbnail &&
            (contentType === ContentType.Link ||
              contentType === ContentType.Image) && (
              <>
                {previewThumbnailImageUrl ? (
                  <img
                    src={previewThumbnailImageUrl}
                    alt="link preview thumbnail"
                    className={classNames(
                      'mr-4 overflow-hidden rounded-md object-cover',
                      activityOrResourceOption &&
                        'max-h-[60px] min-h-[60px] min-w-[60px] max-w-[60px]',
                      !activityOrResourceOption &&
                        'max-h-[48px] min-h-[48px] min-w-[48px] max-w-[48px]',
                    )}
                  />
                ) : (
                  <Thumbnail
                    ThumbnailImage={
                      contentType === ContentType.Link
                        ? ThumbnailLink
                        : ThumbnailImage
                    }
                    activityOrResourceOption={activityOrResourceOption}
                  />
                )}
              </>
            )}
          {!hideThumbnail && contentType === ContentType.PDF && (
            <Thumbnail
              ThumbnailImage={ThumbnailPDF}
              activityOrResourceOption={activityOrResourceOption}
            />
          )}
          {!hideThumbnail && contentType === ContentType.PdfForm && (
            <Thumbnail
              ThumbnailImage={ThumbnailPdfForm}
              activityOrResourceOption={activityOrResourceOption}
            />
          )}
          {!hideThumbnail && contentType === ContentType.Esign && (
            <Thumbnail
              ThumbnailImage={ThumbnailPDFESign}
              activityOrResourceOption={activityOrResourceOption}
            />
          )}
          {!hideThumbnail && contentType === ContentType.Audio && (
            <Thumbnail
              ThumbnailImage={ThumbnailAudio}
              activityOrResourceOption={activityOrResourceOption}
            />
          )}
          {!hideThumbnail && contentType === ContentType.Assessment && (
            <Thumbnail
              ThumbnailImage={ThumbnailAssessment}
              activityOrResourceOption={activityOrResourceOption}
            />
          )}
          {!hideThumbnail && contentType === ContentType.Form && (
            <Thumbnail
              ThumbnailImage={ThumbnailForm}
              activityOrResourceOption={activityOrResourceOption}
            />
          )}
          {!hideThumbnail && contentType === ContentType.Action && (
            <Thumbnail
              ThumbnailImage={ThumbnailAction}
              activityOrResourceOption={activityOrResourceOption}
            />
          )}
          {!hideThumbnail && contentType === ContentType.Text && (
            <Thumbnail
              ThumbnailImage={ThumbnailText}
              activityOrResourceOption={activityOrResourceOption}
            />
          )}
          <div
            className={classNames(
              'flex w-[calc(100%-76px)] flex-col items-start justify-center',
            )}
          >
            {activityOrResourceOption && toggleActivityOrResourceLabel && (
              <div className="flex w-full flex-row items-center justify-between">
                <div
                  className={classNames(
                    'text-left text-small-label font-bold',
                    activityOrResourceOption ===
                      ActivityOrResourceOptions.Activity &&
                      'text-secondary-100',
                    activityOrResourceOption ===
                      ActivityOrResourceOptions.Resource && 'text-neutral-125',
                  )}
                >
                  {activityOrResourceOption.toUpperCase()}
                </div>
              </div>
            )}
            <div
              className={classNames(
                'w-full text-left text-caption font-bold text-green-150',
                description ? 'truncate' : 'line-clamp-2',
                activityOrResourceOption && 'mb-0.5 mt-1',
              )}
            >
              {title}
            </div>
            {description && (
              <div className="w-[96%] truncate text-left text-small-caption text-neutral-110">
                {description}
              </div>
            )}
            {label && (
              <div className="mt-1 rounded-full bg-neutral-75 px-2 py-0.5 text-small-label text-neutral-110">
                {label}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentPreview;

const CompactCardLayout: FC<{
  icon?: React.ReactNode;
  title: string;
  label?: string;
  rows: React.ReactNode;
}> = ({ icon, title, label, rows }) => (
  <>
    <div className={classNames('flex items-start', icon != null && 'gap-2')}>
      {icon}
      <div className="min-w-0 flex-1">
        <div className="text-left text-caption font-bold text-green-150 line-clamp-2">
          {title}
        </div>
        {label && (
          <div className="mt-1 w-fit rounded-full bg-neutral-75 px-2 py-0.5 text-small-label text-neutral-110">
            {label}
          </div>
        )}
      </div>
    </div>
    <div className="mt-3 flex min-w-0 flex-col gap-1.5">{rows}</div>
  </>
);

const Thumbnail: FC<{
  ThumbnailImage: SvgIconComponent;
  activityOrResourceOption?: ActivityOrResourceOptions;
}> = ({ ThumbnailImage, activityOrResourceOption }) => (
  <ThumbnailImage
    className={classNames(
      'mr-4 rounded-md',
      activityOrResourceOption &&
        'max-h-[60px] min-h-[60px] min-w-[60px] max-w-[60px]',
      !activityOrResourceOption &&
        'max-h-[48px] min-h-[48px] min-w-[48px] max-w-[48px]',
    )}
  />
);
